import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import * as Sentry from "@sentry/angular";
import { OAuthErrorEvent, OAuthService } from "angular-oauth2-oidc";
import { BehaviorSubject, combineLatest, Observable } from "rxjs";
import { filter, map } from "rxjs/operators";

@Injectable({ providedIn: "root" })
export class AuthService {
	private isAuthenticatedSubject$ = new BehaviorSubject<boolean>(false);
	public isAuthenticated$ = this.isAuthenticatedSubject$.asObservable();
	private automaticSilentRefreshConfigured = false;

	private isDoneLoadingSubject$ = new BehaviorSubject<boolean>(false);
	public isDoneLoading$ = this.isDoneLoadingSubject$.asObservable();

	/**
	 * Publishes `true` if and only if (a) all the asynchronous initial
	 * login calls have completed or errorred, and (b) the user ended up
	 * being authenticated.
	 *
	 * In essence, it combines:
	 *
	 * - the latest known state of whether the user is authorized
	 * - whether the ajax calls for initial log in have all been done
	 */
	public canActivateProtectedRoutes$: Observable<boolean> = combineLatest([
		this.isAuthenticated$,
		this.isDoneLoading$,
	]).pipe(map((values) => values.every((b) => b)));

	private navigateToLoginPage() {
		this.router.navigateByUrl("/should-login");
	}

	constructor(
		private oauthService: OAuthService,
		private router: Router,
	) {
		// Useful for debugging:
		this.oauthService.events.subscribe((event) => {
			if (event instanceof OAuthErrorEvent) {
				Sentry.captureException(event);
			} else {
				Sentry.addBreadcrumb({ category: "auth", message: `OAuth event: ${event.type}`, level: "info" });
			}
		});

		// THe following cross-tab communication of fresh access tokens works usually in practice,
		// but if you need more robust handling the community has come up with ways to extend logic
		// in the library which may give you better mileage.
		//
		// See: https://github.com/jeroenheijmans/sample-angular-oauth2-oidc-with-auth-guards/issues/2
		//
		// Until then we'll stick to this:
		window.addEventListener("storage", (event) => {
			// The `key` is `null` if the event was caused by `.clear()`
			if (event.key !== "access_token" && event.key !== null) {
				return;
			}

			Sentry.addBreadcrumb({
				category: "auth",
				message: "Noticed changes to access_token (most likely from another tab), updating isAuthenticated",
				level: "info",
			});
			this.isAuthenticatedSubject$.next(this.oauthService.hasValidAccessToken());

			if (!this.oauthService.hasValidAccessToken()) {
				this.navigateToLoginPage();
			}
		});

		this.oauthService.events.subscribe((_) => {
			this.isAuthenticatedSubject$.next(this.oauthService.hasValidAccessToken());
		});
		this.isAuthenticatedSubject$.next(this.oauthService.hasValidAccessToken());

		this.oauthService.events.pipe(filter((e) => ["token_received"].includes(e.type))).subscribe({
			next: () => {
				this.enableAutomaticSilentRefreshIfPossible();
				this.oauthService.loadUserProfile().catch((err) => {
					Sentry.addBreadcrumb({
						category: "auth",
						message: `Could not load user profile (provider may not support OIDC userinfo): ${err}`,
						level: "warning",
					});
				});
			},
		});

		this.oauthService.events
			.pipe(filter((e) => ["session_terminated", "session_error"].includes(e.type)))
			.subscribe((e) => this.navigateToLoginPage());

		this.enableAutomaticSilentRefreshIfPossible();
	}

	private enableAutomaticSilentRefreshIfPossible(): void {
		if (this.automaticSilentRefreshConfigured || !this.oauthService.getRefreshToken()) {
			return;
		}

		this.oauthService.setupAutomaticSilentRefresh();
		this.automaticSilentRefreshConfigured = true;
		Sentry.addBreadcrumb({
			category: "auth",
			message: "Enabled automatic silent refresh",
			level: "info",
		});
	}

	public runInitialLoginSequence(): Promise<void> {
		if (location.hash) {
			const hashParams = location.hash
				.substr(1)
				.split("&")
				.map((kvp) => kvp.split("="))
				.reduce(
					(acc, [key, val]) => {
						acc[key] = val;
						return acc;
					},
					{} as Record<string, string>,
				);
			Sentry.addBreadcrumb({
				category: "auth",
				message: `Encountered hash fragment: ${JSON.stringify(hashParams)}`,
				level: "info",
			});
		}

		// 0. LOAD CONFIG:
		// First we have to check to see how the IdServer is
		// currently configured:
		Sentry.addBreadcrumb({ category: "auth", message: "Loading discovery document", level: "info" });
		return (
			this.oauthService
				.loadDiscoveryDocument()

				// 1. CHECK IF APP_INITIALIZER ALREADY HANDLED CODE FLOW:
				// The APP_INITIALIZER runs tryLoginCodeFlow() which exchanges the
				// authorization code for tokens. If it succeeded we already have a
				// valid access token and can skip the delay + silent refresh below.
				.then(() => {
					if (this.oauthService.hasValidAccessToken()) {
						Sentry.addBreadcrumb({
							category: "auth",
							message: "Token already available from APP_INITIALIZER code flow, skipping implicit login",
							level: "info",
						});
						return Promise.resolve();
					}

					// No token yet — try implicit flow (hash fragment) as fallback.
					// This path is needed when the discovery document is loaded for
					// the first time and no code flow was in progress.
					Sentry.addBreadcrumb({ category: "auth", message: "Trying hash login", level: "info" });
					return this.oauthService.tryLogin().then(() => {});
				})

				.then(() => {
					if (this.oauthService.hasValidAccessToken()) {
						// APP_INITIALIZER already ran tryLoginCodeFlow() which exchanged
						// the authorization code for tokens. No further login needed.
						Sentry.addBreadcrumb({
							category: "auth",
							message: "Valid access token found (code flow handled by APP_INITIALIZER)",
							level: "info",
						});
						return Promise.resolve();
					}

					// 2. SILENT LOGIN:
					// Try to log in via a refresh because then we can prevent
					// needing to redirect the user:
					Sentry.addBreadcrumb({ category: "auth", message: "Trying silent refresh", level: "info" });
					return this.oauthService
						.silentRefresh()
						.then(() => Promise.resolve())
						.catch((result) => {
							// Subset of situations from https://openid.net/specs/openid-connect-core-1_0.html#AuthError
							// Only the ones where it's reasonably sure that sending the
							// user to the IdServer will help.
							const errorResponsesRequiringUserInteraction = [
								"interaction_required",
								"login_required",
								"account_selection_required",
								"consent_required",
							];

							if (result && result.reason && errorResponsesRequiringUserInteraction.indexOf(result.reason.error) >= 0) {
								// 3. ASK FOR LOGIN:
								// At this point we know for sure that we have to ask the
								// user to log in, so we redirect them to the IdServer to
								// enter credentials.
								//
								// Enable this to ALWAYS force a user to login.
								// this.login();
								//
								// Instead, we'll now do this:
								Sentry.captureMessage(
									"User interaction is needed to log in, we will wait for the user to manually log in.",
									{ level: "warning" },
								);
								return Promise.resolve();
							}

							// We can't handle the truth, just pass on the problem to the
							// next handler.
							return Promise.reject(result);
						});
				})

				.then(() => {
					this.isDoneLoadingSubject$.next(true);

					if (!this.oauthService.hasValidAccessToken()) {
						Sentry.captureMessage("OAuth login sequence finished but valid access token is missing", {
							level: "warning",
							extra: { state: this.oauthService.state },
						});
					}

					// Check for the strings 'undefined' and 'null' just to be sure. Our current
					// login(...) should never have this, but in case someone ever calls
					// initImplicitFlow(undefined | null) this could happen.
					if (
						this.oauthService.state &&
						this.oauthService.state !== "undefined" &&
						this.oauthService.state !== "null"
					) {
						let stateUrl = this.oauthService.state;
						if (stateUrl.startsWith("/") === false) {
							stateUrl = decodeURIComponent(stateUrl);
						}
						Sentry.addBreadcrumb({
							category: "auth",
							message: `There was state of ${this.oauthService.state}, intended target is: ${stateUrl}`,
							level: "info",
						});

						// Only skip redirect if we're on /callback (the silent-refresh iframe route).
						// /redirect is the main OAuth callback — we MUST navigate away from it
						// to the user's intended page after successful authentication.
						if (!this.router.url.includes("/callback")) {
							this.router.navigateByUrl(stateUrl);
						}
					}
				})
				.catch(() => this.isDoneLoadingSubject$.next(true))
		);
	}

	public login(targetUrl?: string) {
		// Note: before version 9.1.0 of the library you needed to
		// call encodeURIComponent on the argument to the method.
		this.oauthService.initLoginFlow(targetUrl || this.router.url);
	}

	public logout() {
		this.oauthService.logOut();
	}
	public refresh() {
		this.oauthService.silentRefresh();
	}
	public hasValidToken() {
		return this.oauthService.hasValidAccessToken();
	}

	// These normally won't be exposed from a service like this, but
	// for debugging it makes sense.
	public get accessToken() {
		return this.oauthService.getAccessToken();
	}
	public get refreshToken() {
		return this.oauthService.getRefreshToken();
	}
	public get identityClaims() {
		return this.oauthService.getIdentityClaims();
	}
	public get idToken() {
		return this.oauthService.getIdToken();
	}
	public get logoutUrl() {
		return this.oauthService.logoutUrl;
	}
}
